import React, { useRef, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Dices, ShieldCheck, UserCircle2, Wrench, MessageSquare, Trophy } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import EventsModal from "./EventsModal";

export default function Header() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const isAdmin = user && (user.role === "admin" || user.role === "superadmin");
  const [pendingCount, setPendingCount] = useState(0);
  const clickCount = useRef(0);
  const clickTimeout = useRef(null);

  useEffect(() => {
    if (!isAdmin) return;
    const check = () => {
      api.get("/admin/users").then(({ data }) => {
        setPendingCount(data.filter((u) => !u.approved).length);
      }).catch(() => {});
    };
    check();
    const interval = setInterval(check, 60000);
    window.addEventListener("user-approved", check);
    return () => {
      clearInterval(interval);
      window.removeEventListener("user-approved", check);
    };
  }, [isAdmin]);

  const handleSecretClick = () => {
    clickCount.current += 1;
    clearTimeout(clickTimeout.current);
    clickTimeout.current = setTimeout(() => { clickCount.current = 0; }, 2000);
    if (clickCount.current >= 5) {
      clickCount.current = 0;
      confetti({ particleCount: 180, spread: 90, origin: { y: 0.6 } });
      setTimeout(() => {
        confetti({ particleCount: 100, angle: 60, spread: 65, origin: { x: 0 } });
        confetti({ particleCount: 100, angle: 120, spread: 65, origin: { x: 1 } });
      }, 250);
    }
  };

  return (
    <div className="sticky top-4 z-50 px-4 sm:px-6 lg:px-8 w-full max-w-7xl mx-auto">
      <header
        data-testid="app-header"
        className="bg-white/90 dark:bg-[#151515]/90 backdrop-blur-xl border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-full shadow-sm px-4 sm:px-6 py-2.5 flex items-center justify-between transition-all"
      >
        <Link to="/" className="flex items-center gap-3" data-testid="header-home-link">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[#FDFBF7] shadow-sm transition-all duration-200 ${isHome ? "bg-[#2B593F] ring-2 ring-[#2B593F]/30" : "bg-[#2B593F]"}`}>
            <Dices size={18} strokeWidth={2} />
          </div>
          <div className="hidden sm:block">
            <h1 className={`font-display text-lg font-bold leading-none tracking-tight transition-colors duration-200 ${isHome ? "text-[#2B593F] dark:text-[#8FC79F]" : "text-[#2D2A26] dark:text-[#F5F1EA]"}`}>
              Tavoli da Gioco
              {isHome && <span className="block h-0.5 bg-[#2B593F] dark:bg-[#8FC79F] rounded-full mt-0.5" />}
            </h1>
          </div>
        </Link>

        {user && (
          <div className="flex items-center gap-1 sm:gap-2">
            <EventsModal isAdmin={isAdmin} />
            <NavLink
              to="/taverna"
              title="Taverna"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium transition-colors
                ${isActive
                  ? "bg-[#2B593F]/10 dark:bg-[#2B593F]/20 text-[#2B593F] dark:text-[#8FC79F]"
                  : "text-[#4A4742] dark:text-[#C8C2BA] hover:bg-[#F5F1EA] dark:hover:bg-[#1E1E1E]"}`
              }
            >
              {({ isActive }) => (<>
                <MessageSquare size={16} />
                <span className="hidden md:inline relative">
                  Taverna
                  {isActive && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#2B593F] dark:bg-[#8FC79F] rounded-full" />}
                </span>
              </>)}
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/admin"
                data-testid="header-admin-link"
                title="Pannello admin"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium transition-colors
                  ${isActive
                    ? "bg-[#C8553D]/10 dark:bg-[#C8553D]/20 text-[#C8553D] dark:text-[#F08A78]"
                    : "text-[#C8553D] dark:text-[#F08A78] hover:bg-[#F4DAD3]/40 dark:hover:bg-[#C8553D]/10"}`
                }
              >
                {({ isActive }) => (<>
                  <ShieldCheck size={16} />
                  <span className="hidden md:inline relative">
                    Admin
                    {isActive && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#C8553D] dark:bg-[#F08A78] rounded-full" />}
                  </span>
                  {pendingCount > 0 && (
                    <span className="inline-flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-[#C8553D] dark:bg-[#F08A78] text-[#FDFBF7] dark:text-[#1A0A07] text-[10px] font-bold leading-none">
                      {pendingCount}
                    </span>
                  )}
                </>)}
              </NavLink>
            )}
            {isAdmin && (
              <NavLink
                to="/storico"
                title="Storico partite"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium transition-colors
                  ${isActive
                    ? "bg-[#8A6B1F]/10 dark:bg-[#8A6B1F]/20 text-[#8A6B1F] dark:text-[#D4B87A]"
                    : "text-[#8A6B1F] dark:text-[#D4B87A] hover:bg-[#F5E6BD]/40 dark:hover:bg-[#8A6B1F]/10"}`
                }
              >
                {({ isActive }) => (<>
                  <Trophy size={16} />
                  <span className="hidden md:inline relative">
                    Storico
                    {isActive && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#8A6B1F] dark:bg-[#D4B87A] rounded-full" />}
                  </span>
                </>)}
              </NavLink>
            )}
            <NavLink
              to="/utility"
              data-testid="header-utility-link"
              title="Utility"
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full font-medium transition-colors
                ${isActive
                  ? "bg-[#2B593F]/10 dark:bg-[#2B593F]/20 text-[#2B593F] dark:text-[#8FC79F]"
                  : "text-[#4A4742] dark:text-[#C8C2BA] hover:bg-[#F5F1EA] dark:hover:bg-[#1E1E1E]"}`
              }
            >
              {({ isActive }) => (<>
                <Wrench size={16} />
                <span className="hidden md:inline relative">
                  Utility
                  {isActive && <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#2B593F] dark:bg-[#8FC79F] rounded-full" />}
                </span>
              </>)}
            </NavLink>
            
            <div className="h-4 w-px bg-[#EAE3D9] dark:bg-[#3A3A3A] mx-1 hidden sm:block"></div>

            <Link
              to="/profile"
              data-testid="header-profile-link"
              onClick={handleSecretClick}
              className="inline-flex items-center gap-2 text-sm text-[#2D2A26] dark:text-[#F5F1EA] hover:bg-[#F5F1EA] dark:hover:bg-[#1E1E1E] pl-2 pr-3 py-1.5 rounded-full font-medium transition-colors border border-transparent hover:border-[#EAE3D9] dark:hover:border-[#3A3A3A]"
              title="Il tuo profilo"
            >
              <div className="w-7 h-7 rounded-full bg-[#EAE3D9] dark:bg-[#2A2A2A] flex items-center justify-center text-[#4A4742] dark:text-[#C8C2BA] overflow-hidden">
                {user.avatar ? (
                  <img src={`https://api.dicebear.com/9.x/adventurer/svg?seed=${user.avatar}`} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <UserCircle2 size={16} />
                )}
              </div>
              <span className="hidden md:inline select-none">{user.name}</span>
            </Link>
            
            <ThemeToggle />
            
            <Button
              variant="ghost"
              onClick={logout}
              data-testid="logout-button"
              className="text-[#757169] dark:text-[#B8B2AA] hover:text-[#C8553D] hover:bg-[#F4DAD3]/40 dark:hover:bg-[#C8553D]/10 rounded-full w-9 h-9 p-0 flex items-center justify-center"
              title="Esci"
            >
              <LogOut size={16} />
            </Button>
          </div>
        )}
      </header>
    </div>
  );
}
